import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import {
  Ticket, PlusCircle, UserList, ArrowsLeftRight,
  CheckCircle, ArrowCounterClockwise, ChartBar, Gear, ClipboardText,
  CaretDown,
} from '@phosphor-icons/react';
import { useTicketPermissions } from './hooks/useTicketPermissions';
import { ProductFilterProvider, useProductFilter } from './ProductFilterContext';

import TicketListPage from './pages/TicketListPage';
import TicketNewPage from './pages/TicketNewPage';
import TicketDetailPage from './pages/TicketDetailPage';
import TicketMyTicketsPage from './pages/TicketMyTicketsPage';
import TicketManagerQueuePage from './pages/TicketManagerQueuePage';
import TicketTransferPage from './pages/TicketTransferPage';
import TicketMyTasksPage from './pages/TicketMyTasksPage';
import TicketUpdateStatusPage from './pages/TicketUpdateStatusPage';
import TicketClosurePage from './pages/TicketClosurePage';
import TicketReopenPage from './pages/TicketReopenPage';
import TicketReportsPage from './pages/TicketReportsPage';
import TicketRcaPage from './pages/TicketRcaPage';
import TicketMemberReportPage from './pages/TicketMemberReportPage';
import TicketMasterAdminPage from './pages/admin/TicketMasterAdminPage';
import TicketPipelinePage from './pages/TicketPipelinePage';

function TicketDefaultRedirect() {
  const { allowedPaths, loading } = useTicketPermissions();

  if (loading) {
    return <p style={{ color: '#74777F', fontSize: '13px', padding: '8px' }}>Loading…</p>;
  }

  const paths = allowedPaths || ['list', 'my-tickets', 'new'];
  if (paths.includes('list')) return <Navigate to="list" replace />;
  if (paths.includes('my-tickets')) return <Navigate to="my-tickets" replace />;
  if (paths.includes('my-tasks')) return <Navigate to="my-tasks" replace />;
  if (paths.includes('new')) return <Navigate to="new" replace />;
  return <Navigate to={paths[0] || 'new'} replace />;
}

/** Primary strip — everyday destinations. */
const PRIMARY_NAV = [
  { path: 'new', label: 'New', icon: PlusCircle },
  { path: 'list', label: 'All Tickets', icon: Ticket },
  { path: 'my-tickets', label: 'My Tickets', icon: ClipboardText },
  { path: 'my-tasks', label: 'My Tasks', icon: UserList },
  { path: 'manager-queue', label: 'Queue', icon: UserList },
  { path: 'reports', label: 'Reports', icon: ChartBar },
  { path: 'reports/member', label: 'Members', icon: ChartBar },
  { path: 'rca', label: 'RCA', icon: ChartBar },
  { path: 'admin', label: 'Admin', icon: Gear },
];

/** Secondary — lifecycle actions, tucked into a menu to keep the strip light. */
const ACTION_NAV = [
  { path: 'transfer', label: 'Transfer', icon: ArrowsLeftRight },
  { path: 'update-status', label: 'Update Status', icon: CheckCircle },
  { path: 'closure', label: 'Closure', icon: CheckCircle },
  { path: 'reopen', label: 'Reopen / Close', icon: ArrowCounterClockwise },
];

function SubNavLink({ item }) {
  const { withProductParam } = useProductFilter();
  return (
    <NavLink
      to={withProductParam(`/dashboard/tickets/${item.path}`)}
      end={item.path === 'list'}
      className={({ isActive }) => `tk-subnav-link${isActive ? ' is-active' : ''}`}
    >
      <item.icon size={15} weight={item.path === 'new' ? 'fill' : 'regular'} />
      <span>{item.label}</span>
    </NavLink>
  );
}

function GlobalProductFilter({ pipelineMode = false }) {
  const { productId, setProductId, products, selectedProduct } = useProductFilter();
  // Not allocated to any product → no product filter at all.
  if (!products.length) return null;
  return (
    <div className="tk-global-product" data-testid="tickets-global-product-filter">
      <div className="tk-global-product-main">
        <label htmlFor="tk-global-product-select">Product filter</label>
        <select
          id="tk-global-product-select"
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
        >
          <option value="">All products</option>
          {products.map((p) => (
            <option key={p.product_id} value={p.product_id}>
              {p.name}{p.manager?.name ? ` · ${p.manager.name}` : ''}
            </option>
          ))}
        </select>
      </div>
      <p className="tk-global-product-hint">
        {pipelineMode
          ? (selectedProduct
            ? `Showing 360° for ${selectedProduct.name}. Change product above to switch.`
            : 'Select a product to open its 360° view.')
          : (selectedProduct
            ? `Scoped to ${selectedProduct.name} — status tabs, lists, and Actions below use this filter.`
            : 'Select a product to scope All Tickets, status tabs, and Actions (Transfer, Status, Closure, Reopen).')}
      </p>
      {productId && !pipelineMode && (
        <button type="button" className="tk-btn tk-btn-ghost tk-btn-sm" onClick={() => setProductId('')}>
          Clear product
        </button>
      )}
    </div>
  );
}

function ActionsMenu({ items }) {
  const { withProductParam } = useProductFilter();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const location = useLocation();
  const activeItem = items.find((item) => location.pathname.includes(`/tickets/${item.path}`));

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  };

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReposition = () => updatePosition();
    document.addEventListener('click', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('click', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  if (!items.length) return null;

  return (
    <div className={`tk-subnav-menu${activeItem || open ? ' is-active' : ''}`} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`tk-subnav-link tk-subnav-menu-trigger${activeItem || open ? ' is-active' : ''}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={(e) => {
          e.stopPropagation();
          if (!open) updatePosition();
          setOpen((v) => !v);
        }}
      >
        <ArrowsLeftRight size={15} />
        <span>{activeItem ? activeItem.label : 'Actions'}</span>
        <CaretDown size={12} weight="bold" className={`tk-subnav-caret${open ? ' is-open' : ''}`} />
      </button>
      {open && (
        <div
          className="tk-subnav-dropdown"
          role="menu"
          style={{ top: menuPos.top, right: menuPos.right, left: 'auto' }}
        >
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={withProductParam(`/dashboard/tickets/${item.path}`)}
              role="menuitem"
              className={({ isActive }) => `tk-subnav-dropdown-item${isActive ? ' is-active' : ''}`}
              onClick={() => setOpen(false)}
            >
              <item.icon size={15} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function TicketsShell() {
  const { allowedPaths, loading, canAccess } = useTicketPermissions();
  const location = useLocation();
  const isDetailView = /\/tickets\/list\/[^/]+/.test(location.pathname);
  const isPipelineView = /\/tickets\/pipeline\/?$/.test(location.pathname);
  const pipelineParams = new URLSearchParams(location.search);
  const isAssignee360 = isPipelineView && pipelineParams.has('assigned_to')
    && !pipelineParams.get('product_id');
  const isProduct360 = isPipelineView && !!pipelineParams.get('product_id');
  const pipelineMode = pipelineParams.get('mode') || '';
  const isPerformance360 = isPipelineView && pipelineMode === 'performance'
    && !pipelineParams.get('assigned_to') && !pipelineParams.get('product_id');
  const isDomain360 = isPipelineView && (pipelineMode === 'domain' || !!pipelineParams.get('domain'))
    && !pipelineParams.get('product_id') && !pipelineParams.get('assigned_to');
  const isClient360 = isPipelineView && (pipelineMode === 'client' || !!pipelineParams.get('client'))
    && !pipelineParams.get('product_id') && !pipelineParams.get('assigned_to') && !isDomain360;
  const isVolume360 = isPipelineView && (pipelineMode === 'volume' || !!pipelineParams.get('created_on'))
    && !pipelineParams.get('product_id') && !pipelineParams.get('assigned_to');
  const isSolved360 = isPipelineView && pipelineMode === 'solved';
  const hideProductFilter = isPerformance360 || isDomain360 || isClient360 || isVolume360 || isSolved360;

  const { primaryNav, actionNav } = useMemo(() => {
    const filter = (list) => (
      allowedPaths === null ? list : list.filter((item) => canAccess(item.path))
    );
    return {
      primaryNav: filter(PRIMARY_NAV),
      actionNav: filter(ACTION_NAV),
    };
  }, [allowedPaths, canAccess]);

  const shellTitle = isSolved360 ? 'Tickets solved 360°' : isAssignee360
    ? 'Performance 360°'
    : (isPerformance360
      ? 'Performance by tickets'
      : (isDomain360
        ? 'Tickets by domain'
        : (isClient360
          ? 'Tickets by client'
          : (isVolume360
            ? 'Ticket volume'
            : (isProduct360 || isPipelineView ? 'Product 360°' : 'Tickets')))));
  const shellSubtitle = isSolved360 ? 'Solved tickets · by month, application and person' : isAssignee360
    ? 'Assignee workload · ticket pipeline'
    : (isPerformance360
      ? 'Full leaderboard · click person for assignee 360°'
      : (isDomain360
        ? 'Domain breakdown · click for tickets'
        : (isClient360
          ? 'Client breakdown · click for tickets'
          : (isVolume360
            ? 'Daily volume · click a day for tickets'
            : (isPipelineView
              ? 'Product overview · team · open pipeline'
              : 'Service desk · assignments · lifecycle')))));

  return (
    <div className={`tickets-shell${isDetailView ? ' is-detail' : ''}${isPipelineView ? ' is-pipeline' : ''}`}>
      <div className="tk-module-header">
        <div className="tk-module-brand">
          <div className="tk-module-mark" aria-hidden="true">
            <Ticket size={16} weight="fill" />
          </div>
          <div>
            <h1 className="tk-module-title">{shellTitle}</h1>
            {!isDetailView && (
              <p className="tk-module-subtitle">{shellSubtitle}</p>
            )}
          </div>
        </div>
      </div>

      {!isDetailView && (
        <>
          {!hideProductFilter && (
            <GlobalProductFilter pipelineMode={isPipelineView} />
          )}
          {!isPipelineView && (
            <nav className="tk-subnav" aria-label="Tickets navigation">
              {loading ? (
                <span className="tk-subnav-loading">Loading menu…</span>
              ) : (
                <>
                  <div className="tk-subnav-scroll">
                    {primaryNav.map((item) => (
                      <SubNavLink key={item.path} item={item} />
                    ))}
                  </div>
                  <ActionsMenu items={actionNav} />
                </>
              )}
            </nav>
          )}
        </>
      )}

      <div className="tk-page" key={location.pathname}>
        <Routes>
          <Route index element={<TicketDefaultRedirect />} />
          <Route path="new" element={<TicketNewPage />} />
          <Route path="list" element={<TicketListPage />} />
          <Route path="list/:ticketNo" element={<TicketDetailPage />} />
          <Route path="pipeline" element={<TicketPipelinePage />} />
          <Route path="my-tickets" element={<TicketMyTicketsPage />} />
          <Route path="manager-queue" element={<TicketManagerQueuePage />} />
          <Route path="transfer" element={<TicketTransferPage />} />
          <Route path="my-tasks" element={<TicketMyTasksPage />} />
          <Route path="update-status" element={<TicketUpdateStatusPage />} />
          <Route path="closure" element={<TicketClosurePage />} />
          <Route path="reopen" element={<TicketReopenPage />} />
          <Route path="reports" element={<TicketReportsPage />} />
          <Route path="reports/member" element={<TicketMemberReportPage />} />
          <Route path="rca" element={<TicketRcaPage />} />
          <Route path="admin" element={<TicketMasterAdminPage />} />
          <Route path="*" element={<TicketDefaultRedirect />} />
        </Routes>
      </div>
    </div>
  );
}

function TicketsLayout() {
  return (
    <ProductFilterProvider>
      <TicketsShell />
    </ProductFilterProvider>
  );
}

export default TicketsLayout;
